/**
 * Security Utilities
 * Origin verification, message validation, and security helpers
 */

import {
  isInitMessage,
  type InitMessage,
  type IframeEvent,
} from '../types/communication.types';

// ============================================================================
// Configuration
// ============================================================================

interface SecurityConfig {
  allowedOrigins: string[];
  maxMessageAge: number; // milliseconds
  maxFutureSkew: number; // milliseconds for clock skew
  maxFileSizeMB: number;
  rateLimitRequests: number;
  rateLimitWindowMs: number;
  isDevelopment: boolean;
}

const defaultConfig: SecurityConfig = {
  allowedOrigins: [window.location.origin],
  maxMessageAge: 60000, // 1 minute
  maxFutureSkew: 5000, // 5 seconds
  maxFileSizeMB: 50,
  rateLimitRequests: 100,
  rateLimitWindowMs: 60000, // 1 minute
  isDevelopment: false,
};

let config: SecurityConfig = { ...defaultConfig };

/**
 * Update security configuration
 */
export function configureSecuritySettings(overrides: Partial<SecurityConfig>): void {
  config = { ...config, ...overrides };
}

/**
 * Get current security configuration
 */
export function getSecurityConfig(): Readonly<SecurityConfig> {
  return config;
}

// ============================================================================
// Origin Verification
// ============================================================================

/**
 * Verify if the origin is allowed
 */
export function isOriginAllowed(origin: string): boolean {
  return config.allowedOrigins.includes(origin);
}

/**
 * Verify message origin against allowed origins
 */
export function verifyMessageOrigin(event: MessageEvent): boolean {
  const origin = event.origin;

  if (!isOriginAllowed(origin)) {
    console.warn(`[Security] Rejected message from unauthorized origin: ${origin}`);
    return false;
  }

  return true;
}

// ============================================================================
// Message Validation
// ============================================================================

type AnyMessage = InitMessage | IframeEvent;

/**
 * Validate message structure (INIT or IframeEvent)
 */
export function validateMessage(message: unknown): message is AnyMessage {
  if (typeof message !== 'object' || message === null) return false;

  const msg = message as Record<string, unknown>;

  // Must have type + timestamp at minimum
  if (typeof msg.type !== 'string' || typeof msg.timestamp !== 'number') {
    if (config.isDevelopment) {
      console.warn('[Security] Message missing type/timestamp:', message);
    }
    return false;
  }

  if (!validateTimestamp(msg.timestamp as number)) {
    return false;
  }

  return true;
}

/**
 * Validate message timestamp
 */
export function validateTimestamp(timestamp: number): boolean {
  const now = Date.now();
  const messageAge = now - timestamp;

  if (messageAge > config.maxMessageAge) {
    console.warn('[Security] Message too old:', messageAge, 'ms');
    return false;
  }

  if (messageAge < -config.maxFutureSkew) {
    console.warn('[Security] Message timestamp in future:', -messageAge, 'ms');
    return false;
  }

  return true;
}

/**
 * Sanitize message before processing
 */
export function sanitizeMessage<T extends AnyMessage>(message: T): T {
  // Create a deep copy to avoid reference issues
  const sanitized = JSON.parse(JSON.stringify(message)) as T;

  // Remove any potentially dangerous properties
  const dangerousKeys = ['__proto__', 'constructor', 'prototype'];

  function cleanObject(obj: Record<string, unknown>): void {
    dangerousKeys.forEach((key) => {
      if (key in obj) {
        delete obj[key];
      }
    });

    // Recursively clean nested objects
    Object.values(obj).forEach((value) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        cleanObject(value as Record<string, unknown>);
      }
    });
  }

  cleanObject(sanitized as unknown as Record<string, unknown>);

  return sanitized;
}

/**
 * Full message validation pipeline
 */
export function validateAndSanitizeMessage(
  event: MessageEvent
): AnyMessage | null {
  // Verify origin
  if (!verifyMessageOrigin(event)) {
    return null;
  }

  // Check rate limit
  if (!messageRateLimiter.check(event.origin)) {
    console.warn('[Security] Rate limit exceeded for origin:', event.origin);
    return null;
  }

  // Validate structure
  if (!validateMessage(event.data)) {
    return null;
  }

  // Sanitize and return
  return sanitizeMessage(event.data as AnyMessage);
}

// ============================================================================
// Content Security
// ============================================================================

/**
 * Sanitize HTML content to prevent XSS
 */
export function sanitizeHtmlContent(html: string): string {
  const div = document.createElement('div');
  div.textContent = html;
  return div.innerHTML;
}

/**
 * Validate file size
 */
export function validateFileSize(sizeInBytes: number): boolean {
  const maxSizeBytes = config.maxFileSizeMB * 1024 * 1024;
  return sizeInBytes <= maxSizeBytes;
}

/**
 * Validate base64 string
 */
export function isValidBase64(str: string): boolean {
  if (!str || str.length === 0) {
    return false;
  }

  try {
    return btoa(atob(str)) === str;
  } catch {
    return false;
  }
}

/**
 * Validate JSON string
 */
export function isValidJson(str: string): boolean {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate SFDT content (basic structure check)
 */
export function isValidSfdt(content: string): boolean {
  if (!content || content.length === 0) {
    return false;
  }

  try {
    const parsed = JSON.parse(content);
    if (!parsed || typeof parsed !== 'object') return false;
    // Full SFDT: has sections array
    if ('sections' in parsed) return true;
    // Optimized SFDT (optimizeSfdt=true) uses compact keys like `sec`
    // and is a valid input for editor.open().
    const maybeOptimized = parsed as { sec?: unknown };
    if ('sec' in parsed && Array.isArray(maybeOptimized.sec)) return true;
    // Compressed/packed SFDT: Syncfusion's compact serialization format
    // The sfdt value is a base64-encoded ZIP of the document — editor.open() handles it natively
    const maybeCompressed = parsed as { sfdt?: unknown };
    if ('sfdt' in parsed && typeof maybeCompressed.sfdt === 'string' && maybeCompressed.sfdt.length > 0) return true;
    return false;
  } catch {
    return false;
  }
}

// ============================================================================
// Rate Limiting
// ============================================================================

interface RateLimitEntry {
  count: number;
  resetTime: number;
}

class RateLimiter {
  private limits: Map<string, RateLimitEntry> = new Map();
  private maxRequests: number;
  private windowMs: number;

  constructor(maxRequests: number = 100, windowMs: number = 60000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
  }

  public check(key: string): boolean {
    const now = Date.now();
    const entry = this.limits.get(key);

    if (!entry || now > entry.resetTime) {
      // Create new entry or reset
      this.limits.set(key, {
        count: 1,
        resetTime: now + this.windowMs,
      });
      return true;
    }

    if (entry.count >= this.maxRequests) {
      return false;
    }

    entry.count++;
    return true;
  }

  public reset(key: string): void {
    this.limits.delete(key);
  }

  public clear(): void {
    this.limits.clear();
  }

  public getRemaining(key: string): number {
    const entry = this.limits.get(key);
    if (!entry || Date.now() > entry.resetTime) {
      return this.maxRequests;
    }
    return Math.max(0, this.maxRequests - entry.count);
  }
}

// Singleton rate limiter for message handling
export const messageRateLimiter = new RateLimiter(
  defaultConfig.rateLimitRequests,
  defaultConfig.rateLimitWindowMs
);

// ============================================================================
// Input Validation
// ============================================================================

/**
 * Validate string length
 */
export function validateStringLength(
  str: string,
  minLength: number = 0,
  maxLength: number = 10000
): boolean {
  return str.length >= minLength && str.length <= maxLength;
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate URL format
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Escape special characters for use in regex
 */
export function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Validate user ID format (MongoDB ObjectId)
 */
export function isValidObjectId(id: string): boolean {
  return /^[a-f\d]{24}$/i.test(id);
}

/**
 * Validate document type
 */
export function isValidDocumentType(type: string): type is 'regular' | 'master' | 'control' {
  return ['regular', 'master', 'control'].includes(type);
}

// ============================================================================
// Payload Validation
// ============================================================================

/**
 * Validate EditorConfig payload
 */
export function validateEditorConfig(config: unknown): boolean {
  if (!config || typeof config !== 'object') {
    return false;
  }

  const c = config as Record<string, unknown>;

  // Required fields
  if (!isValidDocumentType(c.documentType as string)) {
    console.warn('[Security] Invalid documentType:', c.documentType);
    return false;
  }

  if (typeof c.canEdit !== 'boolean') {
    console.warn('[Security] Invalid canEdit:', c.canEdit);
    return false;
  }

  // Validate permissions object
  if (!c.permissions || typeof c.permissions !== 'object') {
    console.warn('[Security] Invalid permissions:', c.permissions);
    return false;
  }

  const permissions = c.permissions as Record<string, unknown>;
  const permissionKeys = ['save', 'saveAs', 'export', 'print'];
  for (const key of permissionKeys) {
    if (typeof permissions[key] !== 'boolean') {
      console.warn(`[Security] Invalid permission.${key}:`, permissions[key]);
      return false;
    }
  }

  // Validate comments object
  if (!c.comments || typeof c.comments !== 'object') {
    console.warn('[Security] Invalid comments:', c.comments);
    return false;
  }

  const comments = c.comments as Record<string, unknown>;
  if (!comments.currentUser || typeof comments.currentUser !== 'object') {
    console.warn('[Security] Invalid currentUser:', comments.currentUser);
    return false;
  }

  if (!Array.isArray(comments.mentionableUsers)) {
    console.warn('[Security] Invalid mentionableUsers:', comments.mentionableUsers);
    return false;
  }

  return true;
}

/**
 * Validate LoadDocument payload
 */
export function validateLoadDocumentPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const p = payload as Record<string, unknown>;

  if (typeof p.content !== 'string' || p.content.length === 0) {
    console.warn('[Security] Invalid document content');
    return false;
  }

  if (!['sfdt', 'html', 'docx'].includes(p.format as string)) {
    console.warn('[Security] Invalid document format:', p.format);
    return false;
  }

  // Validate content based on format
  if (p.format === 'sfdt' && !isValidSfdt(p.content as string)) {
    console.warn('[Security] Invalid SFDT content');
    return false;
  }

  return true;
}

/**
 * Validate SetProtection payload
 */
export function validateSetProtectionPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const p = payload as Record<string, unknown>;

  if (typeof p.userId !== 'string' || p.userId.length === 0) {
    console.warn('[Security] Invalid userId for protection');
    return false;
  }

  if (typeof p.userGroup !== 'string' || p.userGroup.length === 0) {
    console.warn('[Security] Invalid userGroup for protection');
    return false;
  }

  return true;
}

// ============================================================================
// Security Logging
// ============================================================================

/**
 * Log security event (only in development)
 */
export function logSecurityEvent(
  event: string,
  details?: Record<string, unknown>
): void {
  if (!config.isDevelopment) {
    return;
  }

  console.log(`[Security] ${event}`, details || '');
}

/**
 * Log security configuration on initialization
 */
export function logSecurityConfig(): void {
  if (!config.isDevelopment) {
    return;
  }

  console.group('[Security] Configuration');
  console.log('Allowed Origins:', config.allowedOrigins);
  console.log('Max Message Age:', config.maxMessageAge, 'ms');
  console.log('Rate Limit:', config.rateLimitRequests, 'per', config.rateLimitWindowMs, 'ms');
  console.log('Max File Size:', config.maxFileSizeMB, 'MB');
  console.log('Development Mode:', config.isDevelopment);
  console.groupEnd();
}